"use client";

import React from "react";
import { ArrowRight, Activity, ShieldCheck, Stethoscope } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const facilities = [{
  title: "High-Speed Canning Line",
  description: "Our state-of-the-art 300 CPM line handles sleek aluminum cans with precision, minimizing oxygen pickup for premium product quality.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1607036/img/canning_line_main.png",
  icon: Activity
}, {
  title: "Counter-Pressure Filling",
  description: "Ensures zero CO2 loss during the filling process, maintaining the perfect fizz for probiotic and adaptogen seltzers.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1607036/img/counter_pressure_valve.png",
  icon: Stethoscope
}, {
  title: "Precision Seamer Tech",
  description: "Double-seam technology creates a hermetic seal, protecting functional ingredients from oxidation and ensuring shelf stability.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1607036/img/automated_seamer.png",
  icon: ShieldCheck
}, {
  title: "In-Line Carbonation",
  description: "Advanced mass-flow meters provide real-time monitoring of CO2 levels for consistent carbonation in every production batch.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1607036/img/carbonation_system.png",
  icon: Activity
}, {
  title: "Compliance & Traceability",
  description: "High-resolution inkjet systems apply precise batch and date codes to meet all FDA traceability and labeling standards.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1607036/img/batch_coding_system.png",
  icon: Stethoscope
}];
export default function AdvancedMedicalFacilities() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: carouselRef,
    isVisible: isCarouselVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="142431" id="advanced-canning-tech" className="relative py-24 bg-background text-foreground overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-primary/5 blur-[120px]" />
        <div className="absolute top-[60%] -left-[10%] w-[40%] h-[40%] rounded-full bg-secondary/5 blur-[100px]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {}
        <div ref={headerRef} className={`flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-mono font-medium uppercase tracking-wider mb-4">
              <Activity className="size-4" />
              <span>Technical Precision</span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-foreground tracking-tighter mb-4">
              Advanced Canning <span className="text-primary">Technology</span>
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              We integrate advanced counter-pressure technology with FDA-registered compliance to deliver high-performance contract manufacturing for the next generation of functional beverages.
            </p>
          </div>
          
          <div className="shrink-0">
            <Button asChild variant="outline" className="rounded-full px-8 border-primary/20 hover:border-primary/50 transition-colors">
              <Link to="/ServicesPage">
                Explore Capabilities
                <ArrowRight className="ml-2 size-4" />
              </Link>
            </Button>
          </div>
        </div>

        {}
        <div ref={carouselRef} className={`transition-all duration-1000 delay-200 ease-out ${isCarouselVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <Carousel opts={{
          align: "start",
          loop: true
        }} className="w-full">
            <CarouselContent className="-ml-4 md:-ml-6">
              {facilities.map((item, i) => <CarouselItem key={i} data-index={i} className="pl-4 md:pl-6 md:basis-1/2 lg:basis-1/3">
                  <div className="relative h-[480px] rounded-[2rem] overflow-hidden group">
                    {}
                    <img src={item.image} alt={item.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    
                    {}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent opacity-70 transition-opacity duration-500 group-hover:opacity-85" />

                    {}
                    <div className="absolute bottom-0 left-0 w-full p-6">
                      <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 transform transition-all duration-500 group-hover:-translate-y-2 group-hover:bg-white/10">
                        <div className="size-10 rounded-full bg-primary/20 flex items-center justify-center mb-4 border border-primary/30">
                          <item.icon className="size-5 text-primary" />
                        </div>
                        <h3 className="text-xl font-semibold text-white mb-2 tracking-tight">
                          {item.title}
                        </h3>
                        <p className="text-white/70 text-sm leading-relaxed line-clamp-3">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  </div>
                </CarouselItem>)}
            </CarouselContent>

            {}
            <div className="flex items-center justify-end gap-3 mt-8">
              <CarouselPrevious className="static translate-y-0 translate-x-0 size-12 rounded-full border-border bg-background hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all shadow-sm" />
              <CarouselNext className="static translate-y-0 translate-x-0 size-12 rounded-full border-border bg-background hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all shadow-sm" />
            </div>
          </Carousel>
        </div>

      </div>
    </section>;
}